"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { roman } from "@/lib/design/tokens";
import { saveProgress, toggleBookmark, saveNote } from "@/lib/actions/reading";
import { artUrl } from "@/lib/svgArt";

type Source = { title: string; detail: string | null; url: string | null; order?: number };
/** A generated illustration facing the rule. Art itself comes from /api/art. */
type Plate = { alt: string; caption: string | null; version: string } | null;
type RelatedRule = { number: number; statement: string };
type Relation = { relation: string; related: RelatedRule | RelatedRule[] | null };
type WorkedExample = { code?: { label: string; bad: boolean; lines: string[] }[] } | null;
type Rule = {
  id: string;
  number: number;
  statement: string;
  why: string | null;
  practice: string | null;
  exceptions: string | null;
  worked_example: WorkedExample;
  rule_sources: Source[];
  rule_relations: Relation[];
  plate?: Plate;
};
type Chapter = {
  id: string;
  number: number;
  title: string;
  blurb: string | null;
  rules_line: string | null;
  rules: Rule[];
};

/** A spread is one open pair of facing pages. */
type Spread =
  | { kind: "title" }
  | { kind: "chapter"; ch: Chapter }
  | { kind: "rule"; ch: Chapter; rule: Rule };

const COPPER = "#B86F44";
const GREEN = "#29483D";
const BADGE_BG = "#E7EDE6";

type Theme = {
  stage: string;
  pageBg: string;
  ink: string;
  soft: string;
  rule: string;
  code: string;
  edge: string;
  topbar: string;
  topbarBorder: string;
  chip: string;
  chipBorder: string;
  chipInk: string;
};

const THEMES: Record<"paper" | "sepia" | "night", Theme> = {
  paper: {
    stage: "#ece2cf",
    pageBg: "#FCFAF5",
    ink: "#202522",
    soft: "#8a8a7d",
    rule: "#e7dcc8",
    code: "#f4edde",
    edge: "rgba(0,0,0,.06)",
    topbar: "rgba(255,255,255,.55)",
    topbarBorder: "rgba(0,0,0,.07)",
    chip: "#FCFAF5",
    chipBorder: "#DED4C3",
    chipInk: "#3d423c",
  },
  sepia: {
    stage: "#e4d4b6",
    pageBg: "#f4ead6",
    ink: "#43382a",
    soft: "#8a7a5c",
    rule: "#e0d3b6",
    code: "#eee1c8",
    edge: "rgba(90,66,20,.09)",
    topbar: "rgba(250,242,225,.6)",
    topbarBorder: "rgba(90,66,20,.14)",
    chip: "#faf2e2",
    chipBorder: "#ddcca9",
    chipInk: "#5a4a30",
  },
  night: {
    stage: "#131714",
    pageBg: "#222925",
    ink: "#e9e4d7",
    soft: "#9aa39a",
    rule: "#39433d",
    code: "#1b201d",
    edge: "rgba(0,0,0,.5)",
    topbar: "rgba(28,34,30,.62)",
    topbarBorder: "rgba(255,255,255,.08)",
    chip: "#2a322d",
    chipBorder: "#39433d",
    chipInk: "#cfd6cd",
  },
};
type ThemeName = keyof typeof THEMES;
const THEME_LABEL: Record<ThemeName, string> = { paper: "Paper", sepia: "Sepia", night: "Night" };

export function Reader({
  bookId,
  slug,
  title,
  author,
  chapters,
  signedIn = false,
  initialSpread = 0,
  initialBookmarks = [],
  initialNotes = {},
}: {
  bookId: string;
  slug: string;
  title: string;
  author: string;
  accent?: string;
  chapters: Chapter[];
  signedIn?: boolean;
  initialSpread?: number;
  initialBookmarks?: string[];
  initialNotes?: Record<string, string>;
}) {
  // Flatten to spreads: a title spread, then per chapter an opener spread and
  // one spread per rule. Each spread paints two facing pages.
  const spreads = useMemo<Spread[]>(() => {
    const s: Spread[] = [{ kind: "title" }];
    for (const ch of chapters) {
      s.push({ kind: "chapter", ch });
      for (const rule of ch.rules) s.push({ kind: "rule", ch, rule });
    }
    return s;
  }, [chapters]);

  const [idx, setIdx] = useState(() => Math.min(Math.max(0, initialSpread), spreads.length - 1));
  const [mode, setMode] = useState<"book" | "reference">("book");
  const [theme, setTheme] = useState<ThemeName>("paper");
  const [scale, setScale] = useState(1);
  const [turn, setTurn] = useState<"next" | "prev" | "">("");
  const [tocOpen, setTocOpen] = useState(false);
  const [tocQuery, setTocQuery] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [bookmarks, setBookmarks] = useState<Set<string>>(() => new Set(initialBookmarks));
  // Notes live in state, not in the server prop: the prop is a snapshot of the
  // page load, so reading straight from it made a note you'd just written come
  // back empty until a full reload.
  const [notes, setNotes] = useState<Record<string, string>>(initialNotes);
  const [panelTab, setPanelTab] = useState<PanelTab>("contents");
  const [narrow, setNarrow] = useState(false);
  const [wide, setWide] = useState(false);

  const t = THEMES[theme];

  // Track the narrow (mobile) breakpoint so we can render one page instead of a
  // spread, and the wide one so the panel can dock beside the book.
  useEffect(() => {
    const narrowMq = window.matchMedia("(max-width: 899px)");
    const wideMq = window.matchMedia("(min-width: 1280px)");
    const on = () => {
      setNarrow(narrowMq.matches);
      setWide(wideMq.matches);
    };
    on();
    narrowMq.addEventListener("change", on);
    wideMq.addEventListener("change", on);
    return () => {
      narrowMq.removeEventListener("change", on);
      wideMq.removeEventListener("change", on);
    };
  }, []);

  // Debounced progress persistence (book mode, signed in).
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!signedIn || spreads.length <= 1) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void saveProgress(bookId, idx / (spreads.length - 1), idx);
    }, 900);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [idx, signedIn, bookId, spreads.length]);

  const current = spreads[idx];
  const currentRuleId = current?.kind === "rule" ? current.rule.id : null;
  const currentRuleNumber = current?.kind === "rule" ? current.rule.number : null;
  const isBookmarked = currentRuleId ? bookmarks.has(currentRuleId) : false;

  // "Share" copies the rule's public page URL; anyone can share, sign-in or not.
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onShareRule = useCallback(async () => {
    if (currentRuleNumber == null) return;
    const url = `${window.location.origin}/books/${slug}/rules/${currentRuleNumber}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard API can reject (focus loss, permissions); fall back to execCommand.
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } finally {
        ta.remove();
      }
    }
    setCopied(true);
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(false), 1600);
  }, [currentRuleNumber, slug]);

  /** Toggle any rule's bookmark — the current spread, or a row in the panel. */
  const toggleBookmarkAt = useCallback(
    (ruleId: string, spreadIdx: number) => {
      if (!signedIn) return;
      const flip = () =>
        setBookmarks((prev) => {
          const nextSet = new Set(prev);
          if (nextSet.has(ruleId)) nextSet.delete(ruleId);
          else nextSet.add(ruleId);
          return nextSet;
        });
      // Optimistic flip, reconciled: revert if the server rejects or throws.
      flip();
      toggleBookmark(bookId, ruleId, spreadIdx)
        .then((res) => {
          if (!res.ok) flip();
        })
        .catch(flip);
    },
    [signedIn, bookId],
  );

  const onToggleBookmark = () => {
    if (!currentRuleId) return;
    toggleBookmarkAt(currentRuleId, idx);
  };

  const go = useCallback(
    (n: number) => {
      setIdx((cur) => {
        const next = Math.max(0, Math.min(spreads.length - 1, n));
        setTurn(next > cur ? "next" : next < cur ? "prev" : "");
        return next;
      });
    },
    [spreads.length],
  );

  const step = useCallback(
    (delta: number) => {
      setIdx((cur) => {
        const next = Math.max(0, Math.min(spreads.length - 1, cur + delta));
        setTurn(next > cur ? "next" : next < cur ? "prev" : "");
        return next;
      });
    },
    [spreads.length],
  );

  /** Step between rule spreads only (reference mode skips title/chapter openers). */
  const stepRule = useCallback(
    (delta: number) => {
      setIdx((cur) => {
        let i = cur + delta;
        while (i >= 0 && i < spreads.length && spreads[i].kind !== "rule") i += delta;
        if (i < 0 || i >= spreads.length) return cur;
        setTurn("");
        return i;
      });
    },
    [spreads],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement && ["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      const move = mode === "book" ? step : stepRule;
      if (e.key === "ArrowRight") move(1);
      if (e.key === "ArrowLeft") move(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, step, stepRule]);

  // Touch swipe (mobile).
  const touchX = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => (touchX.current = e.changedTouches[0].clientX);
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 55) step(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  const progress = spreads.length > 1 ? idx / (spreads.length - 1) : 0;
  const pct = Math.round(progress * 100);
  const subtitle =
    current?.kind === "rule"
      ? `${current.ch.title} · Rule ${current.rule.number}`
      : current?.kind === "chapter"
        ? `Chapter ${roman(current.ch.number)}`
        : "Title page";
  const turnClass = turn === "next" ? "rb-turn-next" : turn === "prev" ? "rb-turn-prev" : "rb-fade";

  return (
    <div className="fixed inset-0 flex flex-col" style={{ background: t.stage, color: t.ink }}>
      {/* Top bar */}
      <header
        className="relative z-30 flex shrink-0 flex-wrap items-center gap-2 border-b px-4 py-2.5 backdrop-blur"
        style={{ borderColor: t.topbarBorder, background: t.topbar }}
      >
        <Link
          href={`/books/${slug}`}
          aria-label="Close reader"
          className="flex h-[34px] w-[34px] items-center justify-center rounded-[9px] text-[18px] leading-none"
          style={{ border: `1px solid ${t.chipBorder}`, background: t.chip, color: t.chipInk }}
        >
          ×
        </Link>
        <div className="mr-1 flex min-w-0 flex-col leading-tight">
          <span className="truncate font-serif text-[15px] font-semibold" style={{ maxWidth: 230 }}>
            {title}
          </span>
          <span className="truncate text-[11px]" style={{ color: t.soft }}>
            {subtitle}
          </span>
        </div>

        <div className="flex-1" />

        {/* Contents — redundant once the rail is docked beside the book. */}
        {!wide && (
          <button
            onClick={() => setTocOpen((v) => !v)}
            className="hidden h-[34px] items-center gap-2 rounded-[9px] px-3 text-[12.5px] font-semibold narrow:flex"
            style={{ border: `1px solid ${t.chipBorder}`, background: t.chip, color: t.chipInk }}
            aria-expanded={tocOpen}
          >
            <span className="text-[13px]">☰</span> Contents
          </button>
        )}

        {/* Search within book */}
        <div
          className="hidden h-[34px] min-w-[190px] items-center gap-1.5 rounded-[9px] px-2 narrow:flex"
          style={{ border: `1px solid ${t.chipBorder}`, background: t.chip }}
        >
          <span className="text-[13px]" style={{ color: t.soft }}>
            ⌕
          </span>
          <input
            value={tocQuery}
            onChange={(e) => {
              setTocQuery(e.target.value);
              if (e.target.value) setTocOpen(true);
            }}
            placeholder="Search within book…"
            className="w-full bg-transparent text-[12.5px] outline-none"
            style={{ color: t.chipInk }}
          />
        </div>

        {/* Text scale */}
        <div
          className="flex h-[34px] items-center overflow-hidden rounded-[9px]"
          style={{ border: `1px solid ${t.chipBorder}`, background: t.chip }}
        >
          <button
            onClick={() => setScale((s) => Math.max(0.88, +(s - 0.08).toFixed(2)))}
            className="h-[34px] w-8 text-[13px]"
            style={{ color: t.chipInk }}
            aria-label="Decrease text size"
          >
            A−
          </button>
          <div className="h-[18px] w-px" style={{ background: t.chipBorder }} />
          <button
            onClick={() => setScale((s) => Math.min(1.28, +(s + 0.08).toFixed(2)))}
            className="h-[34px] w-8 text-[16px]"
            style={{ color: t.chipInk }}
            aria-label="Increase text size"
          >
            A+
          </button>
        </div>

        {/* Theme */}
        <button
          onClick={() => setTheme(theme === "paper" ? "sepia" : theme === "sepia" ? "night" : "paper")}
          className="hidden h-[34px] items-center rounded-[9px] px-3 text-[12.5px] font-semibold narrow:flex"
          style={{ border: `1px solid ${t.chipBorder}`, background: t.chip, color: t.chipInk }}
          title="Reading theme"
        >
          Theme: {THEME_LABEL[theme]}
        </button>

        {signedIn && currentRuleId && (
          <button
            onClick={onToggleBookmark}
            aria-pressed={isBookmarked}
            className="hidden h-[34px] items-center gap-1.5 rounded-[9px] px-3 text-[12.5px] font-semibold narrow:flex"
            style={{
              border: `1px solid ${isBookmarked ? COPPER : t.chipBorder}`,
              background: isBookmarked ? COPPER : t.chip,
              color: isBookmarked ? "#FCFAF5" : t.chipInk,
            }}
          >
            ⚑ {isBookmarked ? "Bookmarked" : "Bookmark"}
          </button>
        )}

        {signedIn && currentRuleId && (
          <button
            onClick={() => setNoteOpen((v) => !v)}
            className="hidden h-[34px] items-center rounded-[9px] px-3 text-[12.5px] font-semibold narrow:flex"
            style={{ border: `1px solid ${t.chipBorder}`, background: t.chip, color: t.chipInk }}
          >
            ✎ Note
          </button>
        )}

        {currentRuleNumber != null && (
          <button
            onClick={onShareRule}
            className="hidden h-[34px] items-center gap-1.5 rounded-[9px] px-3 text-[12.5px] font-semibold narrow:flex"
            style={{
              border: `1px solid ${copied ? COPPER : t.chipBorder}`,
              background: copied ? COPPER : t.chip,
              color: copied ? "#FCFAF5" : t.chipInk,
            }}
            title="Copy a link to this rule"
          >
            {copied ? "✓ Copied" : "⧉ Share"}
          </button>
        )}

        <button
          onClick={() => setMode(mode === "book" ? "reference" : "book")}
          className="h-[34px] rounded-[9px] px-4 text-[12.5px] font-bold"
          style={{ background: GREEN, color: "#FCFAF5" }}
        >
          {mode === "book" ? "Reference Mode" : "Book Mode"}
        </button>
      </header>

      {/* Stage — on wide screens the panel docks beside the book instead of
          covering it, so contents/bookmarks/notes stay visible while reading. */}
      <div className="relative flex min-h-0 flex-1">
      <div
        className="relative min-w-0 flex-1 overflow-y-auto"
        style={{ perspective: 1800 }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {mode === "book" ? (
          <div className="flex min-h-full items-start justify-center px-4 py-8 narrow:px-5 narrow:py-9">
            {narrow ? (
              <SinglePage key={idx} spread={current} theme={t} scale={scale} turnClass={turnClass} title={title} author={author} onNext={() => step(1)} />
            ) : (
              <Spread2Up
                key={idx}
                spread={current}
                theme={t}
                scale={scale}
                turnClass={turnClass}
                title={title}
                author={author}
                idx={idx}
                bookmarked={isBookmarked}
                canBookmark={signedIn}
                onToggleBookmark={onToggleBookmark}
                onNext={() => step(1)}
              />
            )}
          </div>
        ) : (
          <ReferenceMode
            chapters={chapters}
            theme={t}
            scale={scale}
            current={current}
            onJumpToRule={(ruleNumber) => {
              const i = spreads.findIndex((p) => p.kind === "rule" && p.rule.number === ruleNumber);
              if (i >= 0) go(i);
            }}
            onStepRule={stepRule}
          />
        )}
      </div>

        {wide && (
          <ReaderRail
            chapters={chapters}
            spreads={spreads}
            theme={t}
            query={tocQuery}
            setQuery={setTocQuery}
            tab={panelTab}
            setTab={setPanelTab}
            bookmarks={bookmarks}
            notes={notes}
            signedIn={signedIn}
            currentIdx={idx}
            onJump={(n) => {
              setMode("book");
              go(n);
            }}
            onToggleBookmarkAt={toggleBookmarkAt}
          />
        )}
      </div>

      {/* Bottom controls (book mode, desktop) */}
      {mode === "book" && (
        <div
          className="hidden shrink-0 items-center justify-center gap-5 border-t px-5 py-3.5 narrow:flex"
          style={{ borderColor: t.topbarBorder, background: t.topbar }}
        >
          <RoundNav dir="prev" disabled={idx === 0} onClick={() => step(-1)} theme={t} />
          <div className="flex min-w-[220px] flex-col items-center gap-1.5">
            <div className="text-[12px] font-semibold" style={{ color: t.soft }}>
              {idx + 1} of {spreads.length} · {pct}%
            </div>
            <div className="h-1 w-[220px] overflow-hidden rounded-full" style={{ background: t.rule }}>
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: COPPER }} />
            </div>
          </div>
          <RoundNav dir="next" disabled={idx === spreads.length - 1} onClick={() => step(1)} theme={t} />
        </div>
      )}

      {/* Bottom bar (book mode, mobile) */}
      {mode === "book" && (
        <div
          className="flex shrink-0 items-center gap-2 border-t px-3.5 py-2.5 narrow:hidden"
          style={{ borderColor: t.topbarBorder, background: t.topbar }}
        >
          <MobileBtn theme={t} label="☰" aria="Contents" onClick={() => setTocOpen(true)} />
          <MobileBtn theme={t} label="‹" aria="Previous page" onClick={() => step(-1)} disabled={idx === 0} />
          <div className="flex min-w-0 flex-1 flex-col items-center gap-1">
            <div className="whitespace-nowrap text-[11px] font-semibold" style={{ color: t.soft }}>
              {idx + 1} of {spreads.length} · {pct}%
            </div>
            <div className="h-1 w-full max-w-[160px] overflow-hidden rounded-full" style={{ background: t.rule }}>
              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: COPPER }} />
            </div>
          </div>
          <MobileBtn theme={t} label="›" aria="Next page" onClick={() => step(1)} disabled={idx === spreads.length - 1} />
          <MobileBtn theme={t} label="Aa" aria="Display and tools" onClick={() => setSheetOpen(true)} />
        </div>
      )}

      {/* Contents drawer — only below the docked breakpoint; above it the rail
          is always on screen and this would be a second copy of the same thing. */}
      {tocOpen && !wide && (
        <ContentsDrawer
          chapters={chapters}
          spreads={spreads}
          theme={t}
          query={tocQuery}
          setQuery={setTocQuery}
          tab={panelTab}
          setTab={setPanelTab}
          bookmarks={bookmarks}
          notes={notes}
          signedIn={signedIn}
          currentIdx={idx}
          onJump={(n) => {
            setMode("book");
            go(n);
            setTocOpen(false);
          }}
          onToggleBookmarkAt={toggleBookmarkAt}
          onClose={() => setTocOpen(false)}
        />
      )}

      {/* Mobile display & tools sheet */}
      {sheetOpen && (
        <DisplaySheet
          theme={t}
          themeName={theme}
          setTheme={setTheme}
          scale={scale}
          setScale={setScale}
          onScaleDown={() => setScale((s) => Math.max(0.88, +(s - 0.08).toFixed(2)))}
          onScaleUp={() => setScale((s) => Math.min(1.28, +(s + 0.08).toFixed(2)))}
          canBookmark={signedIn && !!currentRuleId}
          isBookmarked={isBookmarked}
          onToggleBookmark={onToggleBookmark}
          onOpenNote={() => {
            setSheetOpen(false);
            setNoteOpen(true);
          }}
          canShare={currentRuleNumber != null}
          copied={copied}
          onShare={onShareRule}
          onClose={() => setSheetOpen(false)}
        />
      )}

      {/* Note panel */}
      {noteOpen && signedIn && currentRuleId && (
        <NotePanel
          key={currentRuleId}
          bookId={bookId}
          ruleId={currentRuleId}
          initialBody={notes[currentRuleId] ?? ""}
          theme={t}
          onSaved={(ruleId, body) =>
            setNotes((prev) => {
              const next = { ...prev };
              if (body.trim()) next[ruleId] = body;
              else delete next[ruleId];
              return next;
            })
          }
          onClose={() => setNoteOpen(false)}
        />
      )}
    </div>
  );
}

/* ============================================================ Book pages */

function pageNumbers(idx: number): [number, number] | null {
  if (idx === 0) return null; // title spread is unnumbered
  return [(idx - 1) * 2 + 1, (idx - 1) * 2 + 2];
}

function Spread2Up({
  spread,
  theme,
  scale,
  turnClass,
  title,
  author,
  idx,
  bookmarked,
  canBookmark,
  onToggleBookmark,
  onNext,
}: {
  spread: Spread;
  theme: Theme;
  scale: number;
  turnClass: string;
  title: string;
  author: string;
  idx: number;
  bookmarked: boolean;
  canBookmark: boolean;
  onToggleBookmark: () => void;
  onNext: () => void;
}) {
  const nums = pageNumbers(idx);

  return (
    <div className={`relative w-[min(940px,96vw)] ${turnClass}`}>
      {/* Ribbon bookmark. It rides in the page's right-hand padding gutter
          (pages are px-[42px]) so it never crosses the text measure, and it's a
          real control: click to bookmark this rule, click again to remove.
          Unbookmarked it tucks up to a nub; bookmarked it drapes. */}
      {spread.kind === "rule" && (
        <button
          type="button"
          onClick={canBookmark ? onToggleBookmark : undefined}
          disabled={!canBookmark}
          aria-pressed={bookmarked}
          aria-label={bookmarked ? "Remove bookmark on this rule" : "Bookmark this rule"}
          title={
            canBookmark
              ? bookmarked
                ? "Bookmarked — click to remove"
                : "Bookmark this rule"
              : "Sign in to bookmark"
          }
          className="absolute z-[8] transition-all duration-500 enabled:hover:brightness-110 disabled:cursor-default"
          style={{
            top: -30,
            right: 16,
            width: 24,
            height: bookmarked ? 108 : 44,
            background: COPPER,
            opacity: bookmarked ? 1 : 0.5,
            clipPath: "polygon(0 0,100% 0,100% 100%,50% 78%,0 100%)",
            boxShadow: "-2px 5px 9px rgba(0,0,0,.28)",
            cursor: canBookmark ? "pointer" : "default",
          }}
        />
      )}

      {/* Page-stack depth — offset sheets peeking below/right so the spread reads as a bound book */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ transform: "translate(6px,7px)", borderRadius: "5px 7px 7px 5px", background: theme.pageBg, boxShadow: `0 30px 58px -34px rgba(32,37,34,.5), inset 0 0 0 1px ${theme.edge}` }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ transform: "translate(3px,3.5px)", borderRadius: "5px 7px 7px 5px", background: theme.pageBg, boxShadow: `inset 0 0 0 1px ${theme.edge}` }}
      />

      <div
        className="relative flex overflow-hidden"
        style={{
          borderRadius: "5px 7px 7px 5px",
          boxShadow: `0 34px 66px -30px rgba(32,37,34,.6),0 3px 8px rgba(0,0,0,.12),inset 0 0 0 1px ${theme.edge}`,
        }}
      >
        {/* Center spine seam — soft crease where the two pages meet */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-1/2 z-10 w-11 -translate-x-1/2"
          style={{ background: "linear-gradient(90deg, transparent, rgba(0,0,0,.05) 42%, rgba(0,0,0,.11) 50%, rgba(0,0,0,.05) 58%, transparent)" }}
        />
        {/* LEFT PAGE (verso) */}
        <div
          className="rb-page-scroll rb-paper-grain flex min-h-[600px] flex-1 flex-col overflow-y-auto px-[42px] pb-8 pt-11"
          style={{ background: theme.pageBg, maxHeight: "calc(100vh - 210px)", boxShadow: `inset -26px 0 30px -26px rgba(0,0,0,.16)` }}
        >
          <RunningHead theme={theme}>
            {spread.kind === "rule" || spread.kind === "chapter" ? spread.ch.title : title}
          </RunningHead>
          <div className="flex-1" style={{ fontSize: `${scale}rem` }}>
            {spread.kind === "title" && <TitleLeft theme={theme} author={author} />}
            {spread.kind === "chapter" && <ChapterLeft ch={spread.ch} theme={theme} />}
            {spread.kind === "rule" && <RuleLeft rule={spread.rule} ch={spread.ch} theme={theme} />}
          </div>
          <PageFolio theme={theme} n={nums?.[0]} />
        </div>

        {/* RIGHT PAGE (recto) */}
        <div
          className="rb-page-scroll rb-paper-grain flex min-h-[600px] flex-1 flex-col overflow-y-auto px-[42px] pb-8 pt-11"
          style={{ background: theme.pageBg, maxHeight: "calc(100vh - 210px)", boxShadow: `inset 26px 0 30px -26px rgba(0,0,0,.16)` }}
        >
          <RunningHead theme={theme} right>
            {title}
          </RunningHead>
          <div className="flex-1" style={{ fontSize: `${scale}rem` }}>
            {spread.kind === "title" && <TitleRight theme={theme} title={title} author={author} />}
            {spread.kind === "chapter" && <ChapterRight ch={spread.ch} theme={theme} />}
            {spread.kind === "rule" && <RuleRight rule={spread.rule} theme={theme} onNext={onNext} />}
          </div>
          <PageFolio theme={theme} n={nums?.[1]} />
        </div>
      </div>
    </div>
  );
}

function SinglePage({
  spread,
  theme,
  scale,
  turnClass,
  title,
  author,
  onNext,
}: {
  spread: Spread;
  theme: Theme;
  scale: number;
  turnClass: string;
  title: string;
  author: string;
  onNext: () => void;
}) {
  return (
    <div
      className={`rb-paper-grain w-full max-w-[600px] rounded-[10px] px-6 pb-8 pt-7 ${turnClass}`}
      style={{
        background: theme.pageBg,
        minHeight: "62vh",
        boxShadow: `0 20px 42px -26px rgba(32,37,34,.55),inset 0 0 0 1px ${theme.edge}`,
        fontSize: `${scale}rem`,
      }}
    >
      {spread.kind === "title" && (
        <div className="flex min-h-[52vh] flex-col items-center justify-center text-center">
          <div className="mb-4 h-[3px] w-10 rounded" style={{ background: COPPER }} />
          <h1 className="font-serif text-[2.4em] font-semibold leading-[1.08] tracking-tight">{title}</h1>
          <p className="mt-4 text-[1em]" style={{ color: theme.soft }}>
            by {author}
          </p>
          <p className="mt-9 text-[.8em]" style={{ color: theme.soft }}>
            Swipe or use the arrows to turn pages.
          </p>
        </div>
      )}
      {spread.kind === "chapter" && (
        <>
          <ChapterLeft ch={spread.ch} theme={theme} />
          <div className="my-7 h-px" style={{ background: theme.rule }} />
          <ChapterRight ch={spread.ch} theme={theme} />
        </>
      )}
      {spread.kind === "rule" && (
        <>
          <RuleLeft rule={spread.rule} ch={spread.ch} theme={theme} />
          <div className="my-6 h-px" style={{ background: theme.rule }} />
          <RuleRight rule={spread.rule} theme={theme} onNext={onNext} />
        </>
      )}
    </div>
  );
}

/* ---- page content blocks ---- */

function TitleLeft({ theme, author }: { theme: Theme; author: string }) {
  return (
    <div className="flex h-full flex-col justify-end pb-6">
      <div className="mb-3 h-[2px] w-11" style={{ background: COPPER }} />
      <p className="font-serif text-[1.05em] italic" style={{ color: theme.soft }}>
        by {author}
      </p>
      <p className="mt-6 text-[.78em]" style={{ color: theme.soft }}>
        Use ← → or the arrows to turn pages.
      </p>
    </div>
  );
}

function TitleRight({ theme, title, author }: { theme: Theme; title: string; author: string }) {
  return (
    <div className="flex h-full flex-col justify-center">
      <div className="mb-5 text-[11px] font-bold uppercase tracking-[.14em]" style={{ color: COPPER }}>
        A Rulebound edition
      </div>
      <h1 className="font-serif text-[2.6em] font-semibold leading-[1.06] tracking-tight" style={{ color: theme.ink }}>
        {title}
      </h1>
      <p className="mt-5 text-[1em]" style={{ color: theme.soft }}>
        {author}
      </p>
    </div>
  );
}

function ChapterLeft({ ch, theme }: { ch: Chapter; theme: Theme }) {
  return (
    <div className="flex h-full flex-col justify-center">
      <div className="mb-3.5 text-[12px] font-bold uppercase tracking-[.14em]" style={{ color: COPPER }}>
        Chapter {roman(ch.number)}
      </div>
      <div className="mb-4 font-serif text-[2.4em] font-semibold leading-[1.08] tracking-tight" style={{ color: theme.ink }}>
        {ch.title}
      </div>
      <div className="mb-5 h-[2px] w-11" style={{ background: COPPER }} />
      {ch.blurb && (
        <p className="max-w-[26em] font-serif text-[1.05em] leading-relaxed" style={{ color: theme.ink }}>
          {ch.blurb}
        </p>
      )}
      {ch.rules_line && (
        <p className="mt-5 text-[.85em]" style={{ color: theme.soft }}>
          {ch.rules_line}
        </p>
      )}
    </div>
  );
}

function ChapterRight({ ch, theme }: { ch: Chapter; theme: Theme }) {
  return (
    <div>
      <SectionLabel>In this chapter</SectionLabel>
      <div className="mt-3 flex flex-col gap-2.5">
        {ch.rules.map((r) => (
          <div key={r.id} className="flex items-baseline gap-3">
            <span className="w-6 shrink-0 text-right font-serif text-[1em] font-semibold" style={{ color: COPPER }}>
              {r.number}
            </span>
            <span className="text-[.98em] leading-snug" style={{ color: theme.ink }}>
              {r.statement}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RuleLeft({ rule, ch, theme }: { rule: Rule; ch: Chapter; theme: Theme }) {
  return (
    <div>
      <div className="mb-3 flex items-center gap-2.5">
        <span className="font-serif text-[.95em] font-semibold" style={{ color: COPPER }}>
          Rule {rule.number}
        </span>
        <span
          className="rounded-full px-2.5 py-[3px] text-[.62em] font-bold uppercase tracking-wide"
          style={{ background: BADGE_BG, color: GREEN }}
        >
          Chapter {roman(ch.number)}
        </span>
      </div>
      <h2 className="mb-5 font-serif text-[1.9em] font-semibold leading-[1.12] tracking-tight" style={{ color: theme.ink }}>
        {rule.statement}
      </h2>
      {rule.why && (
        <PageSection label="Why this rule exists" theme={theme}>
          {rule.why}
        </PageSection>
      )}
      <PlateFigure rule={rule} theme={theme} />
    </div>
  );
}

/** The plate: engraved line work in copper, so it reads on paper and at night. */
function PlateFigure({ rule, theme }: { rule: Rule; theme: Theme }) {
  if (!rule.plate) return null;
  return (
    <figure className="mt-7 mb-1">
      <div className="h-px w-full" style={{ background: theme.rule }} />
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={artUrl("plate", rule.id, rule.plate.version)}
        alt={rule.plate.alt}
        className="mx-auto mt-4 block h-auto w-full max-w-[420px]"
      />
      <figcaption className="mt-3 text-center font-serif text-[.8em] italic" style={{ color: theme.soft }}>
        {rule.plate.caption ? `Plate ${rule.number} — ${rule.plate.caption}` : `Plate ${rule.number}`}
      </figcaption>
    </figure>
  );
}

function RuleRight({ rule, theme, onNext }: { rule: Rule; theme: Theme; onNext: () => void }) {
  const code = rule.worked_example?.code ?? [];
  const relations = rule.rule_relations ?? [];
  const sources = rule.rule_sources ?? [];
  const hasBody = !!rule.practice || code.length > 0 || rule.exceptions || relations.length > 0 || sources.length > 0;

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1">
        {rule.practice && (
          <PageSection label="In practice" theme={theme}>
            {rule.practice}
          </PageSection>
        )}

        {code.length > 0 && (
          <div className="mb-6">
            <SectionLabel>Example</SectionLabel>
            <div className="mt-3 flex flex-col gap-3">
              {code.map((block, i) => (
                <div key={i} className="overflow-hidden rounded-[9px]" style={{ border: `1px solid ${theme.rule}` }}>
                  <div
                    className="flex items-center justify-between px-3 py-1.5 text-[.68em] font-bold uppercase tracking-wide"
                    style={{ background: block.bad ? "rgba(184,111,68,.14)" : "rgba(41,72,61,.1)", color: block.bad ? COPPER : GREEN }}
                  >
                    <span>{block.label}</span>
                    <span>{block.bad ? "✕" : "✓"}</span>
                  </div>
                  <pre
                    className="rb-page-scroll overflow-x-auto px-3.5 py-3 text-[.7em] leading-relaxed"
                    style={{ background: theme.code, color: theme.ink, fontFamily: "ui-monospace,Menlo,monospace" }}
                  >
                    <code>{block.lines.join("\n")}</code>
                  </pre>
                </div>
              ))}
            </div>
          </div>
        )}

        {rule.exceptions && (
          <PageSection label="When it doesn't apply" theme={theme}>
            {rule.exceptions}
          </PageSection>
        )}

        {relations.length > 0 && (
          <div className="mt-6">
            <SectionLabel>Related rules</SectionLabel>
            <div className="mt-3 flex flex-col gap-2.5">
              {relations.map((rel, i) => {
                const target = Array.isArray(rel.related) ? rel.related[0] : rel.related;
                if (!target) return null;
                return (
                  <div
                    key={i}
                    className="flex items-center gap-3 rounded-[9px] px-3.5 py-2.5"
                    style={{ border: `1px solid ${theme.rule}` }}
                  >
                    <span className="w-6 shrink-0 font-serif text-[.95em] font-semibold" style={{ color: COPPER }}>
                      {target.number}
                    </span>
                    <span className="flex-1 text-[.86em] font-semibold" style={{ color: theme.ink }}>
                      {target.statement}
                    </span>
                    <span className="text-[.66em] font-bold uppercase tracking-wide" style={{ color: theme.soft }}>
                      {rel.relation}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {sources.length > 0 && (
          <div className="mt-6">
            <SectionLabel>Sources &amp; further reading</SectionLabel>
            <div className="mt-3 flex flex-col gap-3">
              {sources.map((s, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <span
                    className="mt-0.5 shrink-0 rounded-[6px] px-1.5 py-0.5 text-[.6em] font-bold uppercase"
                    style={{ color: COPPER, border: `1px solid #E4C9B3` }}
                  >
                    Ref
                  </span>
                  <p className="text-[.84em] leading-relaxed" style={{ color: theme.soft }}>
                    {s.url ? (
                      <a href={s.url} target="_blank" rel="noreferrer" className="font-semibold hover:underline" style={{ color: theme.ink }}>
                        {s.title}
                      </a>
                    ) : (
                      <span className="font-semibold" style={{ color: theme.ink }}>
                        {s.title}
                      </span>
                    )}
                    {s.detail ? ` — ${s.detail}` : ""}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {!hasBody && (
          <div className="flex h-full min-h-[220px] flex-col items-center justify-center text-center">
            <div className="mb-2 text-[1.6em]" style={{ color: theme.rule }}>
              ❦
            </div>
            <p className="max-w-[20em] text-[.86em] leading-relaxed" style={{ color: theme.soft }}>
              This rule stands on its own. Turn the page to continue.
            </p>
          </div>
        )}
      </div>

      <div className="mt-6 rounded-[11px] p-4" style={{ border: `1px solid ${theme.rule}` }}>
        <div className="mb-1.5 text-[.68em] uppercase tracking-[.1em]" style={{ color: theme.soft }}>
          Keep reading
        </div>
        <button
          onClick={onNext}
          className="text-[13px] font-bold"
          style={{ color: "#FCFAF5", background: GREEN, borderRadius: 9, padding: "8px 16px" }}
        >
          Turn the page →
        </button>
      </div>
    </div>
  );
}

/* ---- small building blocks ---- */

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[.72em] font-bold uppercase tracking-[.13em]" style={{ color: COPPER }}>
      {children}
    </div>
  );
}

function PageSection({ label, theme, children }: { label: string; theme: Theme; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <SectionLabel>{label}</SectionLabel>
      <p className="mt-2.5 text-[.98em] leading-[1.7]" style={{ color: theme.ink }}>
        {children}
      </p>
    </div>
  );
}

function RunningHead({ children, theme, right }: { children: React.ReactNode; theme: Theme; right?: boolean }) {
  return (
    <div
      className={`mb-6 truncate text-[10.5px] uppercase tracking-[.12em] ${right ? "text-right" : ""}`}
      style={{ color: theme.soft }}
    >
      {children}
    </div>
  );
}

function PageFolio({ theme, n }: { theme: Theme; n?: number }) {
  return (
    <div className="mt-3.5 text-center text-[11px]" style={{ color: theme.soft }}>
      {n ?? ""}
    </div>
  );
}

function RoundNav({ dir, disabled, onClick, theme }: { dir: "prev" | "next"; disabled: boolean; onClick: () => void; theme: Theme }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={dir === "prev" ? "Previous page" : "Next page"}
      className="flex h-11 w-11 items-center justify-center rounded-full text-[18px] disabled:opacity-30"
      style={{ border: `1px solid ${theme.chipBorder}`, background: theme.chip, color: GREEN, boxShadow: "0 3px 8px -3px rgba(32,37,34,.3)" }}
    >
      {dir === "prev" ? "‹" : "›"}
    </button>
  );
}

function MobileBtn({
  theme,
  label,
  aria,
  onClick,
  disabled,
}: {
  theme: Theme;
  label: string;
  aria: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={aria}
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] text-[16px] disabled:opacity-30"
      style={{ border: `1px solid ${theme.chipBorder}`, background: theme.chip, color: theme.chipInk }}
    >
      {label}
    </button>
  );
}

/* ============================================================ Reader panel */

export type PanelTab = "contents" | "bookmarks" | "notes";

const TAB_LABEL: Record<PanelTab, string> = {
  contents: "Contents",
  bookmarks: "Bookmarks",
  notes: "Notes",
};

type PanelProps = {
  chapters: Chapter[];
  spreads: Spread[];
  theme: Theme;
  query: string;
  setQuery: (q: string) => void;
  tab: PanelTab;
  setTab: (t: PanelTab) => void;
  bookmarks: Set<string>;
  notes: Record<string, string>;
  signedIn: boolean;
  currentIdx: number;
  onJump: (n: number) => void;
  onToggleBookmarkAt: (ruleId: string, spreadIdx: number) => void;
};

/** Every rule spread, flattened once so all three tabs can index into it. */
function useRuleIndex(spreads: Spread[]) {
  return useMemo(
    () =>
      spreads
        .map((s, i) => (s.kind === "rule" ? { idx: i, rule: s.rule, ch: s.ch } : null))
        .filter((x): x is { idx: number; rule: Rule; ch: Chapter } => !!x),
    [spreads],
  );
}

function PanelBody(props: PanelProps) {
  const { chapters, spreads, theme, query, setQuery, tab, bookmarks, notes, currentIdx, onJump, onToggleBookmarkAt } = props;
  const q = query.trim().toLowerCase();
  const ruleIndex = useRuleIndex(spreads);
  const spreadOfChapter = (chId: string) => spreads.findIndex((p) => p.kind === "chapter" && p.ch.id === chId);
  const spreadOfRule = (ruleId: string) => spreads.findIndex((p) => p.kind === "rule" && p.rule.id === ruleId);

  const bookmarked = ruleIndex.filter((r) => bookmarks.has(r.rule.id));
  const noted = ruleIndex.filter((r) => (notes[r.rule.id] ?? "").trim().length > 0);

  return (
    <>
      {tab === "contents" && (
        <div className="px-5 pb-3">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search within book…"
            className="w-full rounded-[9px] px-3 py-2 text-[13px] outline-none"
            style={{ border: `1px solid ${theme.rule}`, background: theme.code, color: theme.ink }}
          />
        </div>
      )}

      <div className="rb-page-scroll flex-1 overflow-y-auto px-5 pb-6">
        {tab === "contents" &&
          chapters.map((ch) => {
            const matchedRules = ch.rules.filter(
              (r) => !q || r.statement.toLowerCase().includes(q) || ch.title.toLowerCase().includes(q),
            );
            if (q && matchedRules.length === 0) return null;
            return (
              <div key={ch.id} className="mb-5">
                <button
                  onClick={() => onJump(spreadOfChapter(ch.id))}
                  className="text-left font-serif text-[16px] font-medium"
                  style={{ color: theme.ink }}
                >
                  <span style={{ color: COPPER }}>{roman(ch.number)}. </span>
                  {ch.title}
                </button>
                <ul className="mt-2 space-y-1.5">
                  {matchedRules.map((r) => {
                    const at = spreadOfRule(r.id);
                    return (
                      <li key={r.id}>
                        <button
                          onClick={() => onJump(at)}
                          className="flex w-full gap-2.5 rounded-[6px] px-1 py-0.5 text-left text-[13px]"
                          style={{
                            color: theme.soft,
                            background: at === currentIdx ? theme.code : "transparent",
                          }}
                        >
                          <span className="w-5 shrink-0 text-right" style={{ color: COPPER }}>
                            {r.number}
                          </span>
                          <span className="flex-1">{r.statement}</span>
                          {bookmarks.has(r.id) && <span style={{ color: COPPER }}>⚑</span>}
                          {(notes[r.id] ?? "").trim() && <span style={{ color: theme.soft }}>✎</span>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}

        {tab === "bookmarks" &&
          (bookmarked.length === 0 ? (
            <PanelEmpty theme={theme}>
              No bookmarks yet. Click the ribbon at the top of a page — or the ⚑ button — to mark a
              rule you want to come back to. You can keep as many as you like.
            </PanelEmpty>
          ) : (
            <ul className="space-y-2.5">
              {bookmarked.map(({ idx, rule, ch }) => (
                <li key={rule.id} className="flex items-start gap-2">
                  <button
                    onClick={() => onJump(idx)}
                    className="flex-1 rounded-[8px] px-2 py-1.5 text-left"
                    style={{ background: idx === currentIdx ? theme.code : "transparent" }}
                  >
                    <div className="text-[11px] uppercase tracking-[.1em]" style={{ color: theme.soft }}>
                      {ch.title} · Rule {rule.number}
                    </div>
                    <div className="mt-0.5 font-serif text-[14px]" style={{ color: theme.ink }}>
                      {rule.statement}
                    </div>
                  </button>
                  <button
                    onClick={() => onToggleBookmarkAt(rule.id, idx)}
                    aria-label={`Remove bookmark on rule ${rule.number}`}
                    title="Remove bookmark"
                    className="mt-1.5 shrink-0 text-[13px]"
                    style={{ color: COPPER }}
                  >
                    ⚑
                  </button>
                </li>
              ))}
            </ul>
          ))}

        {tab === "notes" &&
          (noted.length === 0 ? (
            <PanelEmpty theme={theme}>
              No notes yet. Open a rule and use the ✎ Note button to write a private note — it&apos;s
              only ever visible to you.
            </PanelEmpty>
          ) : (
            <ul className="space-y-2.5">
              {noted.map(({ idx, rule, ch }) => (
                <li key={rule.id}>
                  <button
                    onClick={() => onJump(idx)}
                    className="w-full rounded-[8px] px-2 py-1.5 text-left"
                    style={{ background: idx === currentIdx ? theme.code : "transparent" }}
                  >
                    <div className="text-[11px] uppercase tracking-[.1em]" style={{ color: theme.soft }}>
                      {ch.title} · Rule {rule.number}
                    </div>
                    <div className="mt-0.5 font-serif text-[13.5px]" style={{ color: theme.ink }}>
                      {rule.statement}
                    </div>
                    <div
                      className="mt-1 line-clamp-3 whitespace-pre-wrap text-[12.5px]"
                      style={{ color: theme.soft, borderLeft: `2px solid ${COPPER}`, paddingLeft: 8 }}
                    >
                      {notes[rule.id]}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ))}
      </div>
    </>
  );
}

function PanelEmpty({ theme, children }: { theme: Theme; children: React.ReactNode }) {
  return (
    <p className="px-1 py-4 text-[13px] leading-relaxed" style={{ color: theme.soft }}>
      {children}
    </p>
  );
}

function PanelTabs({ tab, setTab, signedIn, theme }: { tab: PanelTab; setTab: (t: PanelTab) => void; signedIn: boolean; theme: Theme }) {
  const tabs: PanelTab[] = signedIn ? ["contents", "bookmarks", "notes"] : ["contents"];
  if (tabs.length === 1) return null;
  return (
    <div className="flex gap-1 px-5 pb-3" role="tablist">
      {tabs.map((k) => (
        <button
          key={k}
          role="tab"
          aria-selected={tab === k}
          onClick={() => setTab(k)}
          className="rounded-[8px] px-2.5 py-1 text-[12px] font-semibold"
          style={{
            background: tab === k ? COPPER : "transparent",
            color: tab === k ? "#FCFAF5" : theme.soft,
          }}
        >
          {TAB_LABEL[k]}
        </button>
      ))}
    </div>
  );
}

/** Docked beside the book on wide screens — no overlay, no dismissal. */
function ReaderRail(props: PanelProps) {
  return (
    <aside
      className="flex h-full w-[320px] shrink-0 flex-col border-l"
      style={{ background: props.theme.pageBg, borderColor: props.theme.rule }}
    >
      <div className="px-5 pb-3 pt-5 text-[12px] font-bold uppercase tracking-[.14em]" style={{ color: props.theme.soft }}>
        In this book
      </div>
      <PanelTabs tab={props.tab} setTab={props.setTab} signedIn={props.signedIn} theme={props.theme} />
      <PanelBody {...props} />
    </aside>
  );
}

/** Overlay drawer for everything narrower than the docked breakpoint. */
function ContentsDrawer(props: PanelProps & { onClose: () => void }) {
  const { onClose, ...panel } = props;
  return (
    <>
      <div className="absolute inset-0 z-40 bg-black/25" onClick={onClose} />
      <aside
        className="absolute right-0 top-0 z-50 flex h-full w-[340px] max-w-[86vw] flex-col border-l"
        style={{ background: panel.theme.pageBg, borderColor: panel.theme.rule }}
      >
        <div className="flex items-center justify-between px-5 pb-3 pt-5">
          <div className="text-[12px] font-bold uppercase tracking-[.14em]" style={{ color: panel.theme.soft }}>
            In this book
          </div>
          <button onClick={onClose} aria-label="Close contents" className="text-[18px]" style={{ color: panel.theme.soft }}>
            ×
          </button>
        </div>
        <PanelTabs tab={panel.tab} setTab={panel.setTab} signedIn={panel.signedIn} theme={panel.theme} />
        <PanelBody {...panel} />
      </aside>
    </>
  );
}

/* ============================================================ Note panel */

function NotePanel({
  bookId,
  ruleId,
  initialBody,
  theme,
  onSaved,
  onClose,
}: {
  bookId: string;
  ruleId: string;
  initialBody: string;
  theme: Theme;
  onSaved: (ruleId: string, body: string) => void;
  onClose: () => void;
}) {
  const [body, setBody] = useState(initialBody);
  const [saved, setSaved] = useState(initialBody);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  async function commit() {
    // Compare against what's actually persisted, not the mount-time prop —
    // otherwise a second edit in the same session silently no-ops.
    if (body === saved) return;
    setStatus("saving");
    try {
      const res = await saveNote(bookId, ruleId, body);
      if (res.ok) {
        setSaved(body);
        // Lift it so the notes tab and the ✎ markers see it without a reload.
        onSaved(ruleId, body);
      }
      setStatus(res.ok ? "saved" : "error");
    } catch {
      setStatus("error");
    }
  }

  return (
    <div
      className="absolute bottom-5 right-5 z-50 w-[min(360px,92vw)] rounded-[12px] p-4 shadow-2xl"
      style={{ background: theme.pageBg, border: `1px solid ${theme.rule}` }}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[.72em] font-bold uppercase tracking-[.14em]" style={{ color: COPPER }}>
          Your note
        </span>
        <div className="flex items-center gap-2" aria-live="polite">
          {status === "saving" && (
            <span className="text-[11px]" style={{ color: theme.soft }}>
              Saving…
            </span>
          )}
          {status === "saved" && (
            <span className="text-[11px]" style={{ color: theme.soft }}>
              Saved
            </span>
          )}
          {status === "error" && (
            <button onClick={commit} className="text-[11px] font-semibold" style={{ color: "var(--danger)" }}>
              Couldn&apos;t save — retry
            </button>
          )}
          <button onClick={onClose} aria-label="Close note" className="text-[16px]" style={{ color: theme.soft }}>
            ×
          </button>
        </div>
      </div>
      <textarea
        value={body}
        autoFocus
        onChange={(e) => {
          setBody(e.target.value);
          setStatus("idle");
        }}
        onBlur={commit}
        rows={4}
        placeholder="Write a private note on this rule…"
        className="w-full resize-y rounded-[8px] bg-transparent p-2.5 text-[13px] outline-none"
        style={{ border: `1px solid ${theme.rule}`, color: theme.ink }}
      />
    </div>
  );
}

/* ============================================================ Display sheet (mobile) */

function DisplaySheet({
  theme,
  themeName,
  setTheme,
  onScaleDown,
  onScaleUp,
  canBookmark,
  isBookmarked,
  onToggleBookmark,
  onOpenNote,
  canShare,
  copied,
  onShare,
  onClose,
}: {
  theme: Theme;
  themeName: ThemeName;
  setTheme: (t: ThemeName) => void;
  scale: number;
  setScale: (fn: (s: number) => number) => void;
  onScaleDown: () => void;
  onScaleUp: () => void;
  canBookmark: boolean;
  isBookmarked: boolean;
  onToggleBookmark: () => void;
  onOpenNote: () => void;
  canShare: boolean;
  copied: boolean;
  onShare: () => void;
  onClose: () => void;
}) {
  const row = "flex items-center justify-between";
  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/40" onClick={onClose} style={{ animation: "rbFade .2s ease" }} />
      <div
        role="dialog"
        aria-label="Display and tools"
        className="fixed inset-x-0 bottom-0 z-[65] flex flex-col gap-5 rounded-t-[18px] px-5 pb-8 pt-3"
        style={{ background: theme.pageBg, boxShadow: "0 -16px 44px -14px rgba(32,37,34,.45)", animation: "rbUp .3s cubic-bezier(.3,1,.4,1)" }}
      >
        <div className="mx-auto h-[5px] w-11 rounded-full" style={{ background: theme.rule }} />

        <div className={row}>
          <span className="text-[12px] font-bold uppercase tracking-[.12em]" style={{ color: theme.soft }}>
            Theme
          </span>
          <div className="flex gap-1.5">
            {(Object.keys(THEMES) as ThemeName[]).map((name) => (
              <button
                key={name}
                onClick={() => setTheme(name)}
                className="rounded-full px-3.5 py-1.5 text-[13px] font-semibold"
                style={
                  name === themeName
                    ? { background: COPPER, color: "#FCFAF5" }
                    : { border: `1px solid ${theme.rule}`, color: theme.ink }
                }
              >
                {THEME_LABEL[name]}
              </button>
            ))}
          </div>
        </div>

        <div className={row}>
          <span className="text-[12px] font-bold uppercase tracking-[.12em]" style={{ color: theme.soft }}>
            Text size
          </span>
          <div className="flex items-center overflow-hidden rounded-[10px]" style={{ border: `1px solid ${theme.rule}` }}>
            <button onClick={onScaleDown} className="h-9 w-12 text-[14px]" style={{ color: theme.ink }} aria-label="Decrease text size">
              A−
            </button>
            <div className="h-5 w-px" style={{ background: theme.rule }} />
            <button onClick={onScaleUp} className="h-9 w-12 text-[18px]" style={{ color: theme.ink }} aria-label="Increase text size">
              A+
            </button>
          </div>
        </div>

        {canBookmark && (
          <div className="flex gap-2.5">
            <button
              onClick={() => {
                onToggleBookmark();
              }}
              aria-pressed={isBookmarked}
              className="flex h-11 flex-1 items-center justify-center gap-2 rounded-[11px] text-[13px] font-bold"
              style={{
                border: `1px solid ${isBookmarked ? COPPER : theme.rule}`,
                background: isBookmarked ? COPPER : "transparent",
                color: isBookmarked ? "#FCFAF5" : theme.ink,
              }}
            >
              ⚑ {isBookmarked ? "Bookmarked" : "Bookmark"}
            </button>
            <button
              onClick={onOpenNote}
              className="flex h-11 flex-1 items-center justify-center gap-2 rounded-[11px] text-[13px] font-bold"
              style={{ border: `1px solid ${theme.rule}`, color: theme.ink }}
            >
              ✎ Note
            </button>
          </div>
        )}

        {canShare && (
          <button
            onClick={onShare}
            className="flex h-11 items-center justify-center gap-2 rounded-[11px] text-[13px] font-bold"
            style={{
              border: `1px solid ${copied ? COPPER : theme.rule}`,
              background: copied ? COPPER : "transparent",
              color: copied ? "#FCFAF5" : theme.ink,
            }}
          >
            {copied ? "✓ Link copied" : "⧉ Share this rule"}
          </button>
        )}
      </div>
    </>
  );
}

/* ============================================================ Reference mode */

/**
 * Deep single-rule view (per the prototype): a sticky "On this rule" anchor
 * nav beside the full record — why, practice, example, exceptions, related
 * rules, and sources. Follows the book-mode spread; related rules jump.
 */
function ReferenceMode({
  chapters,
  theme,
  scale,
  current,
  onJumpToRule,
  onStepRule,
}: {
  chapters: Chapter[];
  theme: Theme;
  scale: number;
  current: Spread;
  onJumpToRule: (ruleNumber: number) => void;
  onStepRule: (delta: number) => void;
}) {
  // Resolve the rule in focus from the current spread.
  const ch =
    current.kind === "rule" || current.kind === "chapter" ? current.ch : chapters[0];
  const rule = current.kind === "rule" ? current.rule : (ch?.rules[0] ?? null);
  if (!ch || !rule) return null;

  const code = rule.worked_example?.code ?? [];
  const relations = rule.rule_relations ?? [];
  const sources = rule.rule_sources ?? [];
  const allRules = chapters.flatMap((c) => c.rules);
  const ruleIdx = allRules.findIndex((r) => r.id === rule.id);

  const sections = [
    { id: "why", label: "Why this rule exists", show: !!rule.why },
    { id: "plate", label: "Plate", show: !!rule.plate },
    { id: "practice", label: "In practice", show: !!rule.practice },
    { id: "example", label: "Example", show: code.length > 0 },
    { id: "exceptions", label: "When it doesn't apply", show: !!rule.exceptions },
    { id: "related", label: "Related rules", show: relations.length > 0 },
    { id: "sources", label: "Sources", show: sources.length > 0 },
  ].filter((s) => s.show);

  const jumpTo = (id: string) =>
    document.getElementById(`ref-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div
      className="mx-auto flex max-w-[1100px] items-start gap-7 px-5 py-8 narrow:px-7 narrow:py-10"
      style={{ fontSize: `${scale}rem` }}
    >
      {/* On this rule — sticky anchor nav (desktop) */}
      <aside className="sticky top-8 hidden w-[190px] shrink-0 narrow:block">
        <div
          className="rounded-[14px] p-5"
          style={{ background: theme.chip, border: `1px solid ${theme.chipBorder}` }}
        >
          <div className="mb-3 text-[.68em] font-bold uppercase tracking-[.13em]" style={{ color: theme.soft }}>
            On this rule
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => jumpTo("principle")}
              className="text-left text-[.88em] font-semibold hover:underline"
              style={{ color: theme.ink }}
            >
              Principle
            </button>
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => jumpTo(s.id)}
                className="text-left text-[.88em] font-semibold hover:underline"
                style={{ color: theme.ink }}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </aside>

      {/* The rule record */}
      <article
        className="min-w-0 flex-1 rounded-[16px] px-6 py-7 narrow:px-10 narrow:py-9"
        style={{
          background: theme.pageBg,
          border: `1px solid ${theme.edge}`,
          boxShadow: "0 18px 34px -24px rgba(32,37,34,.35)",
        }}
      >
        <div id="ref-principle" className="mb-2 flex flex-wrap items-center gap-3">
          <span className="font-serif text-[1.05em] font-semibold" style={{ color: COPPER }}>
            Rule {rule.number}
          </span>
          <span
            className="rounded-full px-2.5 py-[3px] text-[.62em] font-bold uppercase tracking-wide"
            style={{ background: BADGE_BG, color: GREEN }}
          >
            Chapter {roman(ch.number)}
          </span>
          <span className="text-[.85em]" style={{ color: theme.soft }}>
            {ch.title}
          </span>
          <span className="flex-1" />
          <span className="flex items-center gap-1.5">
            <button
              onClick={() => onStepRule(-1)}
              disabled={ruleIdx <= 0}
              aria-label="Previous rule"
              className="h-[30px] w-[30px] rounded-full text-[13px] disabled:opacity-35"
              style={{ border: `1px solid ${theme.chipBorder}`, background: theme.chip, color: theme.chipInk }}
            >
              ‹
            </button>
            <button
              onClick={() => onStepRule(1)}
              disabled={ruleIdx === allRules.length - 1}
              aria-label="Next rule"
              className="h-[30px] w-[30px] rounded-full text-[13px] disabled:opacity-35"
              style={{ border: `1px solid ${theme.chipBorder}`, background: theme.chip, color: theme.chipInk }}
            >
              ›
            </button>
          </span>
        </div>

        <h1
          className="mb-7 max-w-[24em] font-serif text-[2.1em] font-semibold leading-[1.1] tracking-tight"
          style={{ color: theme.ink }}
        >
          {rule.statement}
        </h1>

        {rule.why && (
          <div id="ref-why" className="mb-7 scroll-mt-6">
            <SectionLabel>Why this rule exists</SectionLabel>
            <p className="mt-2.5 max-w-[46em] text-[1em] leading-[1.75]" style={{ color: theme.ink }}>
              {rule.why}
            </p>
          </div>
        )}

        {rule.plate && (
          <div id="ref-plate" className="mb-7 scroll-mt-6">
            <SectionLabel>Plate</SectionLabel>
            <PlateFigure rule={rule} theme={theme} />
          </div>
        )}

        {rule.practice && (
          <div id="ref-practice" className="mb-7 scroll-mt-6">
            <SectionLabel>In practice</SectionLabel>
            <p className="mt-2.5 max-w-[46em] text-[1em] leading-[1.75]" style={{ color: theme.ink }}>
              {rule.practice}
            </p>
          </div>
        )}

        {code.length > 0 && (
          <div id="ref-example" className="mb-7 scroll-mt-6">
            <SectionLabel>Example</SectionLabel>
            <div className={`mt-3 grid gap-3 ${code.length > 1 ? "narrow:grid-cols-2" : ""}`}>
              {code.map((block, i) => (
                <div key={i} className="min-w-0 overflow-hidden rounded-[9px]" style={{ border: `1px solid ${theme.rule}` }}>
                  <div
                    className="flex items-center justify-between px-3 py-1.5 text-[.68em] font-bold uppercase tracking-wide"
                    style={{
                      background: block.bad ? "rgba(184,111,68,.14)" : "rgba(41,72,61,.1)",
                      color: block.bad ? COPPER : GREEN,
                    }}
                  >
                    <span>{block.label}</span>
                    <span>{block.bad ? "✕" : "✓"}</span>
                  </div>
                  <pre
                    className="rb-page-scroll overflow-x-auto px-3.5 py-3 text-[.72em] leading-relaxed"
                    style={{ background: theme.code, color: theme.ink, fontFamily: "ui-monospace,Menlo,monospace" }}
                  >
                    <code>{block.lines.join("\n")}</code>
                  </pre>
                </div>
              ))}
            </div>
          </div>
        )}

        {rule.exceptions && (
          <div id="ref-exceptions" className="mb-7 scroll-mt-6">
            <SectionLabel>When it doesn&apos;t apply</SectionLabel>
            <p className="mt-2.5 max-w-[46em] text-[1em] leading-[1.75]" style={{ color: theme.ink }}>
              {rule.exceptions}
            </p>
          </div>
        )}

        {relations.length > 0 && (
          <div id="ref-related" className="mb-7 scroll-mt-6">
            <SectionLabel>Related rules</SectionLabel>
            <div className="mt-3 flex max-w-[46em] flex-col gap-2.5">
              {relations.map((rel, i) => {
                const target = Array.isArray(rel.related) ? rel.related[0] : rel.related;
                if (!target) return null;
                return (
                  <button
                    key={i}
                    onClick={() => onJumpToRule(target.number)}
                    className="flex items-center gap-3 rounded-[9px] px-3.5 py-2.5 text-left transition-colors hover:underline"
                    style={{ border: `1px solid ${theme.rule}` }}
                  >
                    <span className="w-6 shrink-0 font-serif text-[.95em] font-semibold" style={{ color: COPPER }}>
                      {target.number}
                    </span>
                    <span className="flex-1 text-[.86em] font-semibold" style={{ color: theme.ink }}>
                      {target.statement}
                    </span>
                    <span className="text-[.66em] font-bold uppercase tracking-wide" style={{ color: theme.soft }}>
                      {rel.relation}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {sources.length > 0 && (
          <div id="ref-sources" className="scroll-mt-6">
            <SectionLabel>Sources &amp; further reading</SectionLabel>
            <div className="mt-3 flex max-w-[46em] flex-col gap-3">
              {sources.map((s, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <span
                    className="mt-0.5 shrink-0 rounded-[6px] px-1.5 py-0.5 text-[.6em] font-bold uppercase"
                    style={{ color: COPPER, border: `1px solid #E4C9B3` }}
                  >
                    Ref
                  </span>
                  <p className="text-[.88em] leading-relaxed" style={{ color: theme.soft }}>
                    <span className="font-semibold" style={{ color: theme.ink }}>
                      {s.url ? (
                        <a href={s.url} target="_blank" rel="noreferrer" className="hover:underline">
                          {s.title}
                        </a>
                      ) : (
                        s.title
                      )}
                    </span>
                    {s.detail ? ` — ${s.detail}` : ""}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}
